import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './CharacterSelection.css';
import { authButtonStyle, authButtonSmallStyle } from '../constants/authStyles';
import { ORIGIN_OPTIONS, selectImageUrl } from '../constants/originSelection';
import { usePopupMessage } from '../hooks/usePopupMessage';
import { useCharacter } from '../context/CharacterContext';
import { hasSeenIntroVideo } from '../utils/introVideo';
import { buildOriginResolutionMap } from '../utils/originSelectionMapping';

const CharacterSelection = () => {
  const { t } = useTranslation('selection');
  const { t: tc } = useTranslation('common');
  const [backgroundReady, setBackgroundReady] = useState(false);
  const [selectedOriginId, setSelectedOriginId] = useState(null);
  const [originResolutionMap, setOriginResolutionMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { setError, clearError } = usePopupMessage();
  const { refreshCharacter, loading: characterLoading, hasCharacter } = useCharacter();
  const navigate = useNavigate();
  const username = localStorage.getItem('username');

  const selectedOrigin = ORIGIN_OPTIONS.find((origin) => origin.id === selectedOriginId) || null;

  useEffect(() => {
    if (!username) {
      navigate('/login', { replace: true });
      return;
    }
    if (!characterLoading && hasCharacter) {
      navigate('/game', { replace: true });
      return;
    }
    if (!characterLoading && !hasCharacter && !hasSeenIntroVideo(username)) {
      navigate('/intro', { replace: true });
    }
  }, [username, navigate, characterLoading, hasCharacter]);

  useEffect(() => {
    const timerId = setTimeout(() => setBackgroundReady(true), 1000);
    return () => clearTimeout(timerId);
  }, []);

  const fetchOriginData = useCallback(async () => {
    try {
      const charactersResponse = await axios.get('/api/characters');
      if (!charactersResponse.data.success) {
        setError(charactersResponse.data.message || t('characterLoadFailed'));
        return;
      }

      const characters = charactersResponse.data.characters || [];
      if (!characters.length) {
        setError(t('characterNotFound'));
        return;
      }

      const abilityResponses = await Promise.all(
        characters.map((character) =>
          axios.get(`/api/ability-types?characterId=${character.id}`)
        )
      );

      const abilityTypesByCharacterId = {};
      characters.forEach((character, index) => {
        const response = abilityResponses[index];
        abilityTypesByCharacterId[character.id] = response.data.success
          ? response.data.abilityTypes || []
          : [];
      });

      const resolutionMap = buildOriginResolutionMap(characters, abilityTypesByCharacterId);
      setOriginResolutionMap(resolutionMap);

      if (Object.keys(resolutionMap).length !== ORIGIN_OPTIONS.length) {
        setError(t('originMappingFailed'));
      }
    } catch {
      setError(t('characterLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [setError, t]);

  useEffect(() => {
    if (!username || characterLoading || hasCharacter) return;
    if (!hasSeenIntroVideo(username)) return;
    fetchOriginData();
  }, [username, characterLoading, hasCharacter, fetchOriginData]);

  const handleReplayIntro = () => {
    if (saving) return;
    navigate('/intro?replay=1');
  };

  const handleOriginSelect = (originId) => {
    if (saving) return;
    clearError();
    setSelectedOriginId(originId);
  };

  const handleConfirmSelection = async () => {
    if (!selectedOriginId || saving) return;

    const resolution = originResolutionMap[selectedOriginId];
    if (!resolution) {
      setError(t('originMappingFailed'));
      return;
    }

    setSaving(true);
    clearError();

    try {
      const response = await axios.post('/api/user-character', {
        username,
        characterId: resolution.characterId,
        abilityTypeId: resolution.abilityTypeId,
      });

      if (response.data.success) {
        await refreshCharacter();
        navigate('/game', { replace: true });
      } else {
        setError(response.data.message || tc('saveFailed'));
        setSaving(false);
      }
    } catch (err) {
      setError(err.response?.data?.message || tc('errorGeneric'));
      setSaving(false);
    }
  };

  if (characterLoading || hasCharacter || loading) {
    return (
      <div className="origin-selection origin-selection--loading">
        <div className="origin-selection__loading">{tc('loading')}</div>
      </div>
    );
  }

  return (
    <div className="origin-selection">
      <div
        className={`origin-selection__bg origin-selection__bg--intro ${backgroundReady ? 'origin-selection__bg--dimmed' : ''}`}
        style={{ backgroundImage: `url("${selectImageUrl('1.png')}")` }}
        aria-hidden="true"
      />
      <div
        className={`origin-selection__bg origin-selection__bg--main ${backgroundReady ? 'origin-selection__bg--visible' : ''}`}
        style={{ backgroundImage: `url("${selectImageUrl('6.png')}")` }}
        aria-hidden="true"
      />

      <button
        type="button"
        className="origin-selection__replay"
        style={authButtonSmallStyle}
        onClick={handleReplayIntro}
        disabled={saving}
      >
        {t('originReplayIntro')}
      </button>

      <div className="origin-selection__footer">
        {backgroundReady && !selectedOriginId && (
          <p className="origin-selection__hint">{t('originPickHint')}</p>
        )}
        <div className="origin-selection__portraits">
          {ORIGIN_OPTIONS.map((origin) => {
            const isSelected = selectedOriginId === origin.id;
            const isDimmed = selectedOriginId && !isSelected;
            const imageSrc = isSelected ? origin.colorImg : origin.grayImg;

            return (
              <button
                key={origin.id}
                type="button"
                className={`origin-selection__portrait ${isSelected ? 'origin-selection__portrait--selected' : ''} ${isDimmed ? 'origin-selection__portrait--dimmed' : ''}`}
                onClick={() => handleOriginSelect(origin.id)}
                disabled={saving}
                aria-pressed={isSelected}
                aria-label={t(origin.titleKey)}
              >
                <span className="origin-selection__portrait-ring" aria-hidden="true" />
                <span className="origin-selection__portrait-shine" aria-hidden="true" />
                <img src={selectImageUrl(imageSrc)} alt="" draggable={false} />
              </button>
            );
          })}
        </div>
      </div>

      {selectedOrigin && (
        <div className="origin-selection__popup" role="dialog" aria-modal="true" aria-labelledby="origin-popup-title">
          <h2 id="origin-popup-title" className="origin-selection__popup-title">
            {t(selectedOrigin.titleKey)}
          </h2>
          <p className="origin-selection__popup-desc">{t(selectedOrigin.descKey)}</p>
          <button
            type="button"
            className="origin-selection__confirm"
            style={authButtonStyle}
            onClick={handleConfirmSelection}
            disabled={saving}
          >
            {saving ? t('originConfirmSaving') : t('originConfirm')}
          </button>
        </div>
      )}
    </div>
  );
};

export default CharacterSelection;
